// src/pages/Careers.js - Redesigned to match main design
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Globe, Rocket, Brush, MapPin, Clock, Send, CheckCircle, X, ChevronRight } from "lucide-react";
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL STYLES - Matching Home.js design system
══════════════════════════════════════════════════════════════════ */
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');
    @font-face { font-family:'Azonix'; src:url('/fonts/Azonix.otf') format('opentype'),url('/fonts/azonix.woff2') format('woff2'); font-display:swap; }
    @font-face { font-family:'Japanese3017'; src:url('/fonts/Japanese-3017.ttf') format('truetype'); font-display:swap; }
    @font-face { font-family:'Acknowledgement'; src:url('/fonts/Acknowledgement.otf') format('opentype'); font-display:swap; }

    :root {
      --az: 'Azonix','Impact',sans-serif;
      --jp: 'Japanese3017','Noto Serif JP',serif;
      --wt: 'Acknowledgement','Azonix',sans-serif;
      --ui: -apple-system,'Helvetica Neue',sans-serif;
      --bd: 'Georgia',serif;
      --neu: #a78bfa;
      --bg: #08080c;
      --glass: rgba(255,255,255,0.04);
      --gb: rgba(255,255,255,0.08);
    }

    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}

    .careers-root {
      background: var(--bg);
      color: #fff;
      font-family: var(--ui);
      overflow-x: hidden;
      min-height: 100vh;
      position: relative;
    }

    .careers-wrap {
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 clamp(16px,4vw,52px);
      position: relative;
      z-index: 1;
    }

    .careers-sec { padding: clamp(56px,8vh,100px) 0; }
    .careers-sec-sm { padding: clamp(36px,5vh,64px) 0; }

    /* Section Header */
    .careers-sh {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 28px;
      gap: 16px;
    }
    .careers-sh-left { display:flex; align-items:center; gap:12px; }
    .careers-sh-bar { width:3px; height:26px; border-radius:2px; flex-shrink:0; }
    .careers-sh-label {
      font-family: var(--az);
      font-size: 16px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    /* Glass Card */
    .careers-card {
      background: var(--glass);
      backdrop-filter: blur(24px);
      border: 1px solid var(--gb);
      border-radius: 16px;
      padding: clamp(20px,3vw,28px);
      transition: all .3s ease;
    }
    .careers-card:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(167,139,250,0.25);
      transform: translateY(-4px);
    }

    /* Job Grid */
    .careers-jobs-grid {
      display: grid;
      grid-template-columns: repeat(4,1fr);
      gap: 16px;
    }
    @media(max-width:1100px){ .careers-jobs-grid { grid-template-columns: repeat(2,1fr); } }
    @media(max-width:600px){ .careers-jobs-grid { grid-template-columns: 1fr; } }

    .careers-job-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 16px;
      padding: 24px;
      cursor: pointer;
      transition: all .35s ease;
    }
    .careers-job-card:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(167,139,250,0.3);
      transform: translateY(-6px);
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    }
    .careers-job-icon {
      width: 48px; height: 48px;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 16px;
    }
    .careers-job-title {
      font-family: var(--az);
      font-size: 15px;
      color: #fff;
      margin-bottom: 8px;
    }
    .careers-job-loc {
      display: flex; align-items: center; gap: 4px;
      font-size: 12px;
      color: rgba(255,255,255,0.38);
      margin-bottom: 8px;
    }
    .careers-job-desc {
      font-size: 13px;
      color: rgba(255,255,255,0.5);
      margin-bottom: 16px;
      line-height: 1.5;
    }
    .careers-job-btn {
      display: block;
      width: 100%;
      padding: 10px;
      border-radius: 8px;
      border: 1px solid rgba(167,139,250,0.32);
      color: var(--neu);
      font-family: var(--az);
      font-size: 9px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      text-align: center;
      transition: all .25s;
    }
    .careers-job-btn:hover {
      background: rgba(167,139,250,0.1);
    }

    /* Perks Grid */
    .careers-perks-grid {
      display: grid;
      grid-template-columns: repeat(5,1fr);
      gap: 14px;
    }
    @media(max-width:900px){ .careers-perks-grid { grid-template-columns: repeat(3,1fr); } }
    @media(max-width:600px){ .careers-perks-grid { grid-template-columns: repeat(2,1fr); } }

    .careers-perk-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 14px;
      padding: 20px;
      text-align: center;
      transition: all .3s ease;
    }
    .careers-perk-card:hover {
      border-color: rgba(167,139,250,0.2);
      transform: translateY(-3px);
    }
    .careers-perk-title {
      font-family: var(--az);
      font-size: 13px;
      color: #fff;
      margin-bottom: 6px;
    }
    .careers-perk-desc {
      font-size: 11px;
      color: rgba(255,255,255,0.38);
    }

    /* Values Grid */
    .careers-values-grid {
      display: grid;
      grid-template-columns: repeat(3,1fr);
      gap: 16px;
    }
    @media(max-width:768px){ .careers-values-grid { grid-template-columns: 1fr; } }

    .careers-value-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 16px;
      padding: 24px;
      transition: all .35s ease;
    }
    .careers-value-card:hover {
      transform: translateY(-4px);
      border-color: rgba(167,139,250,0.2);
    }
    .careers-value-icon {
      width: 48px; height: 48px;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 16px;
    }
    .careers-value-title {
      font-family: var(--az);
      font-size: 16px;
      color: #fff;
      margin-bottom: 8px;
    }
    .careers-value-desc {
      font-size: 13px;
      color: rgba(255,255,255,0.42);
      line-height: 1.6;
    }

    /* Quote Section */
    .careers-quote {
      text-align: center;
      padding: clamp(36px,5vw,56px);
      background: linear-gradient(135deg, rgba(167,139,250,0.06) 0%, rgba(236,72,153,0.04) 100%);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 22px;
    }
    .careers-quote-text {
      font-size: clamp(14px,1.5vw,18px);
      font-style: italic;
      color: rgba(255,255,255,0.8);
      font-family: var(--bd);
      line-height: 1.8;
      max-width: 560px;
      margin: 0 auto 12px;
    }
    .careers-quote-author {
      font-family: var(--az);
      font-size: 11px;
      letter-spacing: 2px;
      text-transform: uppercase;
      background: linear-gradient(135deg, #a78bfa, #ec4899);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    /* CTA */
    .careers-cta {
      max-width: 680px;
      margin: 0 auto;
      border-radius: 22px;
      text-align: center;
      background: var(--glass);
      border: 1px solid var(--gb);
      backdrop-filter: blur(24px);
      padding: clamp(32px,5vw,48px);
      position: relative;
      overflow: hidden;
    }
    .careers-cta-glow {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%,-50%);
      width: 300px; height: 140px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.1), transparent);
      filter: blur(40px);
      pointer-events: none;
    }
    .careers-cta-title {
      position: relative;
      z-index: 1;
      font-family: var(--az);
      font-size: clamp(16px,2.5vw,22px);
      color: #fff;
      margin-bottom: 10px;
    }
    .careers-cta-desc {
      position: relative;
      z-index: 1;
      font-size: 14px;
      color: rgba(255,255,255,0.42);
      max-width: 400px;
      margin: 0 auto 20px;
      line-height: 1.7;
    }
    .careers-btn {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      text-decoration: none;
      font-family: var(--az);
      font-size: 10px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      transition: all .25s;
      border: 1px solid rgba(167,139,250,0.32);
      color: var(--neu);
    }
    .careers-btn:hover {
      background: rgba(167,139,250,0.1);
      border-color: rgba(167,139,250,0.5);
    }

    /* Hero */
    .careers-hero {
      position: relative;
      min-height: 45vh;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    .careers-hero-bg {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(167,139,250,0.08) 0%, rgba(236,72,153,0.04) 50%, rgba(99,102,241,0.06) 100%);
    }
    .careers-hero-glow1 {
      position: absolute;
      top: 20%; left: 15%;
      width: 280px; height: 280px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.12), transparent);
      filter: blur(60px);
    }
    .careers-hero-glow2 {
      position: absolute;
      bottom: 20%; right: 15%;
      width: 220px; height: 220px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(236,72,153,0.1), transparent);
      filter: blur(50px);
    }
    .careers-hero-content {
      position: relative;
      z-index: 1;
      text-align: center;
      padding: 80px 20px 50px;
    }
    .careers-hero-title {
      font-family: var(--az);
      font-size: clamp(36px,6vw,58px);
      letter-spacing: -1px;
      margin-bottom: 16px;
    }
    .careers-hero-title span {
      background: linear-gradient(135deg, var(--neu), #ec4899);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .careers-hero-desc {
      font-size: clamp(14px,1.5vw,17px);
      color: rgba(255,255,255,0.42);
      max-width: 480px;
      margin: 0 auto;
      line-height: 1.8;
    }

    /* Divider */
    .careers-div {
      height: 1px;
      background: linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent);
      margin: 0 0 clamp(48px,7vh,80px);
    }

    /* Modal */
    .careers-modal-overlay {
      position: fixed;
      inset: 0;
      z-index: 200;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(0,0,0,0.8);
      backdrop-filter: blur(8px);
    }
    .careers-modal {
      background: var(--bg);
      border: 1px solid var(--gb);
      border-radius: 20px;
      max-width: 480px;
      width: 100%;
      max-height: 85vh;
      overflow-y: auto;
      position: relative;
    }
    .careers-modal-header {
      position: relative;
      padding: 24px;
      border-bottom: 1px solid var(--gb);
    }
    .careers-modal-close {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 32px; height: 32px;
      border-radius: 50%;
      background: rgba(255,255,255,0.06);
      border: none;
      color: rgba(255,255,255,0.5);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all .2s;
    }
    .careers-modal-close:hover {
      background: rgba(255,255,255,0.12);
      color: #fff;
    }
    .careers-modal-title {
      font-family: var(--az);
      font-size: 20px;
      color: #fff;
      margin-bottom: 8px;
    }
    .careers-modal-meta {
      display: flex;
      gap: 16px;
      font-size: 12px;
      color: rgba(255,255,255,0.42);
    }
    .careers-modal-meta span {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .careers-modal-body {
      padding: 24px;
    }
    .careers-modal-section {
      margin-bottom: 20px;
    }
    .careers-modal-section h4 {
      font-family: var(--az);
      font-size: 12px;
      color: #fff;
      margin-bottom: 10px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    .careers-modal-desc {
      font-size: 14px;
      color: rgba(255,255,255,0.6);
      line-height: 1.7;
      margin-bottom: 16px;
    }
    .careers-modal-list {
      list-style: none;
    }
    .careers-modal-list li {
      font-size: 13px;
      color: rgba(255,255,255,0.5);
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .careers-modal-list li::before {
      content: '';
      width: 6px; height: 6px;
      border-radius: 50%;
      background: var(--neu);
      flex-shrink: 0;
    }

    /* Form */
    .careers-form {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .careers-input {
      width: 100%;
      padding: 14px 16px;
      background: rgba(255,255,255,0.04);
      border: 1px solid var(--gb);
      border-radius: 10px;
      color: #fff;
      font-size: 14px;
      font-family: var(--ui);
      outline: none;
      transition: border-color .2s;
    }
    .careers-input::placeholder {
      color: rgba(255,255,255,0.25);
    }
    .careers-input:focus {
      border-color: rgba(167,139,250,0.4);
    }
    .careers-textarea {
      min-height: 100px;
      resize: vertical;
    }
    .careers-submit {
      width: 100%;
      padding: 14px;
      border-radius: 10px;
      border: none;
      background: linear-gradient(135deg, var(--neu), #8b5cf6);
      color: #fff;
      font-family: var(--az);
      font-size: 10px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      cursor: pointer;
      transition: all .25s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .careers-submit:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(167,139,250,0.4);
    }
    .careers-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .careers-success {
      padding: 16px;
      background: rgba(74,222,128,0.1);
      border: 1px solid rgba(74,222,128,0.2);
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 10px;
      color: #4ade80;
      font-size: 14px;
    }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════
   PARTICLE BACKGROUND
══════════════════════════════════════════════════════════════════ */
const ParticleBackground = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let W = cv.width = window.innerWidth;
    let H = cv.height = window.innerHeight;

    const resize = () => { W = cv.width = window.innerWidth; H = cv.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    const pts = Array.from({ length: 35 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .2, vy: (Math.random() - .5) * .2,
      r: Math.random() * 1.2 + .3, a: Math.random() * .35 + .07,
      h: [250, 270, 280][Math.floor(Math.random() * 3)],
    }));

    const mm = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', mm);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouse.current;
      for (let i = 0; i < pts.length; i++)
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 100) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(167,139,250,${.05 * (1 - d / 100)})`; ctx.lineWidth = .4; ctx.stroke(); }
        }
      pts.forEach(p => {
        const d = Math.hypot(mx - p.x, my - p.y);
        if (d < 90) { p.vx -= (mx - p.x) / d * .028; p.vy -= (my - p.y) / d * .028; }
        p.vx *= .99; p.vy *= .99; p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.h},72%,70%,${p.a})`; ctx.fill();
      });
      const g = ctx.createRadialGradient(mx, my, 0, mx, my, 160);
      g.addColorStop(0, 'rgba(167,139,250,0.04)'); g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      raf.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); window.removeEventListener('mousemove', mm); cancelAnimationFrame(raf.current); };
  }, []);

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: .5 }} />;
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
export default function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({ name: "", email: "", coverLetter: "" });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const jobs = [
    { 
      title: "Frontend Developer", 
      location: "Remote", 
      type: "Full-time",
      icon: Globe,
      color: "#3b82f6",
      description: "Build stunning user interfaces for our web platforms.",
      requirements: ["React/Vue expertise", "Tailwind CSS", "3+ years experience"]
    },
    { 
      title: "3D Animator", 
      location: "Lagos, Nigeria", 
      type: "Full-time",
      icon: Brush,
      color: "#ec4899",
      description: "Create captivating 3D animations and visual effects.",
      requirements: ["Blender/Maya proficiency", "Animation portfolio", "2+ years experience"]
    },
    { 
      title: "Backend Developer", 
      location: "Remote", 
      type: "Full-time",
      icon: Rocket,
      color: "#8b5cf6",
      description: "Architect robust APIs and database systems.",
      requirements: ["Node.js/Python", "Database design", "API development"]
    },
    { 
      title: "UI/UX Designer", 
      location: "Seoul, South Korea", 
      type: "Full-time",
      icon: Brush,
      color: "#10b981",
      description: "Design intuitive and beautiful user experiences.",
      requirements: ["Figma expertise", "User research", "Design systems"]
    },
  ];

  const perks = [
    { title: "Remote First", desc: "Work from anywhere" },
    { title: "Global Team", desc: "Collaborate across continents" },
    { title: "Learning Budget", desc: "Continuous education" },
    { title: "Competitive Pay", desc: "Industry-leading salary" },
    { title: "Health & Wellness", desc: "Comprehensive coverage" },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!applicationForm.name.trim()) newErrors.name = "Name is required";
    if (!applicationForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicationForm.email)) {
      newErrors.email = "Valid email is required";
    }
    if (!applicationForm.coverLetter.trim()) newErrors.coverLetter = "Cover letter is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setSelectedJob(null);
        setApplicationForm({ name: "", email: "", coverLetter: "" });
      }, 3000);
    }
  };

  return (
    <>
      <PageStyles />
      <SEO
        title="Careers at ANIMAC - Join Our Creative Team"
        description="Join ANIMAC's global team of creatives shaping the future of animation in the metaverse. Explore career opportunities in development, design, and animation."
        url="/careers"
        image="/assets/animac-preview-logo.svg"
        type="website"
      />

      <ParticleBackground />

      <div className="careers-root">
        {/* Hero */}
        <div className="careers-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80" 
              alt="Careers" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/85 via-gray-900/75 to-gray-900/85" />
          </div>
          <div className="careers-hero-bg" />
          <div className="careers-hero-glow1" />
          <div className="careers-hero-glow2" />
          <div className="careers-hero-content">
            <motion.h1
              className="careers-hero-title"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              Join <span>ANIMAC</span>
            </motion.h1>
            <motion.p
              className="careers-hero-desc"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Be part of a global creative team shaping the future of animation in the metaverse.
            </motion.p>
          </div>
        </div>

        <div className="careers-wrap">
          {/* Job Openings */}
          <section className="careers-sec">
            <div className="careers-sh">
              <div className="careers-sh-left">
                <div className="careers-sh-bar" style={{ background: 'linear-gradient(to bottom, #a78bfa, transparent)' }} />
                <span className="careers-sh-label" style={{ color: '#a78bfa' }}>Current Openings</span>
              </div>
            </div>
            
            <div className="careers-jobs-grid">
              {jobs.map((job, i) => (
                <motion.div
                  key={i}
                  className="careers-job-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setSelectedJob(job)}
                >
                  <div className="careers-job-icon" style={{ background: `linear-gradient(135deg, ${job.color}22, ${job.color}08)` }}>
                    <job.icon size={22} color={job.color} />
                  </div>
                  <h3 className="careers-job-title">{job.title}</h3>
                  <div className="careers-job-loc">
                    <MapPin size={12} />{job.location}
                  </div>
                  <p className="careers-job-desc">{job.description}</p>
                  <div className="careers-job-btn">View Details</div>
                </motion.div>
              ))}
            </div>
          </section>

          <div className="careers-div" />

          {/* Culture & Perks */}
          <section className="careers-sec">
            <div className="careers-sh">
              <div className="careers-sh-left">
                <div className="careers-sh-bar" style={{ background: 'linear-gradient(to bottom, #ec4899, transparent)' }} />
                <span className="careers-sh-label" style={{ color: '#ec4899' }}>Culture & Perks</span>
              </div>
            </div>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.42)', textAlign: 'center', marginBottom: '24px', fontFamily: 'var(--bd)' }}>
              At ANIMAC, we value creativity, collaboration, and growth. Join our diverse team and enjoy:
            </p>

            <div className="careers-perks-grid">
              {perks.map((perk, i) => (
                <motion.div
                  key={i}
                  className="careers-perk-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                  <h3 className="careers-perk-title">{perk.title}</h3>
                  <p className="careers-perk-desc">{perk.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Values */}
          <section className="careers-sec">
            <div className="careers-values-grid">
              <motion.div
                className="careers-value-card"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="careers-value-icon" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(139,92,246,0.08))' }}>
                  <Globe size={24} color="#8b5cf6" />
                </div>
                <h3 className="careers-value-title">Global Impact</h3>
                <p className="careers-value-desc">Reach millions of users worldwide with your work</p>
              </motion.div>
              <motion.div
                className="careers-value-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="careers-value-icon" style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.2), rgba(236,72,153,0.08))' }}>
                  <Brush size={24} color="#ec4899" />
                </div>
                <h3 className="careers-value-title">Creative Freedom</h3>
                <p className="careers-value-desc">Express your creativity without limits</p>
              </motion.div>
              <motion.div
                className="careers-value-card"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="careers-value-icon" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.2), rgba(59,130,246,0.08))' }}>
                  <Rocket size={24} color="#3b82f6" />
                </div>
                <h3 className="careers-value-title">Innovation First</h3>
                <p className="careers-value-desc">Push boundaries with cutting-edge tech</p>
              </motion.div>
            </div>
          </section>

          {/* Quote */}
          <section className="careers-sec-sm">
            <div className="careers-quote">
              <p className="careers-quote-text">
                "Join ANIMAC, where imagination meets opportunity. Build, innovate, and shape the future of animation."
              </p>
              <p className="careers-quote-author">— ANIMAC Careers</p>
            </div>
          </section>

          {/* CTA */}
          <section className="careers-sec">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="careers-cta"
            >
              <div className="careers-cta-glow" />
              <h2 className="careers-cta-title">Don't See the Right Role?</h2>
              <p className="careers-cta-desc">
                We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities!
              </p>
              <Link to="/contact" className="careers-btn">
                Get in Touch <ChevronRight size={14} />
              </Link>
            </motion.div>
          </section>
        </div>

        {/* Job Modal */}
        {selectedJob && (
          <div className="careers-modal-overlay" onClick={() => setSelectedJob(null)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="careers-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="careers-modal-header">
                <button className="careers-modal-close" onClick={() => setSelectedJob(null)}>
                  <X size={16} />
                </button>
                <h2 className="careers-modal-title">{selectedJob.title}</h2>
                <div className="careers-modal-meta">
                  <span><MapPin size={12} /> {selectedJob.location}</span>
                  <span><Clock size={12} /> {selectedJob.type}</span>
                </div>
              </div>
              <div className="careers-modal-body">
                <div className="careers-modal-section">
                  <p className="careers-modal-desc">{selectedJob.description}</p>
                </div>
                <div className="careers-modal-section">
                  <h4>Requirements</h4>
                  <ul className="careers-modal-list">
                    {selectedJob.requirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>

                {isSubmitted ? (
                  <div className="careers-success">
                    <CheckCircle size={20} />
                    Application submitted successfully!
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="careers-form">
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={applicationForm.name}
                      onChange={(e) => setApplicationForm({...applicationForm, name: e.target.value})}
                      className="careers-input"
                    />
                    {errors.name && <p style={{ color: '#f87171', fontSize: '12px' }}>{errors.name}</p>}
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={applicationForm.email}
                      onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                      className="careers-input"
                    />
                    {errors.email && <p style={{ color: '#f87171', fontSize: '12px' }}>{errors.email}</p>}
                    <textarea
                      placeholder="Cover Letter"
                      rows="3"
                      value={applicationForm.coverLetter}
                      onChange={(e) => setApplicationForm({...applicationForm, coverLetter: e.target.value})}
                      className="careers-input careers-textarea"
                    />
                    {errors.coverLetter && <p style={{ color: '#f87171', fontSize: '12px' }}>{errors.coverLetter}</p>}
                    <button type="submit" className="careers-submit">
                      <Send size={14} /> Submit Application
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
}
